import { useScrollReveal } from '@/components/animations/useScrollPin';
import { renderTextMarkup } from '@/utils/textMarkup';

const TEXT_SIZE_CLASS = {
  body: 'text-base',
  'body-lg': 'text-body-lg',
  h3: 'text-h3',
  h2: 'text-h2',
  h1: 'text-h1',
};

const JUSTIFY_CLASS = { left: 'justify-start', center: 'justify-center', right: 'justify-end' };

// 'grid' = larghezza di lettura contenuta (coerente con gli altri blocchi
// testuali del sito); 'widescreen' = tutta la larghezza disponibile del
// blocco (max-w-6xl del contenitore); 'custom' = percentuale scelta in
// admin via `textWidthCustom` — stessa unità (% del blocco) di `imageWidth`,
// per coerenza tra i due controlli di larghezza di questo blocco.
const TEXT_WIDTH_CLASS = { grid: 'max-w-2xl', widescreen: 'max-w-none w-full' };

/**
 * Immagine e testo indipendenti — MAI usati come sfondo (a differenza di
 * Hero/CTA): l'immagine ha una sua larghezza/posizione/margini, il testo
 * una sua dimensione/posizione/larghezza/allineamento, entrambi animati per
 * conto proprio all'ingresso e all'uscita dal viewport (vedi useScrollReveal
 * in useScrollPin.js — non è uno scroll-pin, l'animazione parte quando
 * l'elemento entra/esce dalla vista, l'elemento non blocca lo scroll).
 * Il testo supporta un micro-markup (**grassetto**, *corsivo*, a capo —
 * vedi src/utils/textMarkup.js), non testo grezzo.
 * @param {{ image?: string, imageAlt?: string, imageWidth?: number, imagePosition?: 'left'|'center'|'right', imageMarginTop?: number, imageMarginRight?: number, imageMarginBottom?: number, imageMarginLeft?: number, imageAnimIn?: string, imageAnimOut?: string, text?: string, textSize?: string, textWidth?: 'grid'|'widescreen'|'custom', textWidthCustom?: number, textPosition?: 'left'|'center'|'right', textAlign?: 'left'|'center'|'right'|'justify', textAnimIn?: string, textAnimOut?: string, marginTop?: number, marginBottom?: number, bgColor?: string, textColor?: string }} props
 */
export function ScrollRevealBlock({
  image, imageAlt = '', imageWidth = 50, imagePosition = 'center',
  imageMarginTop = 0, imageMarginRight = 0, imageMarginBottom = 0, imageMarginLeft = 0,
  imageAnimIn = 'fade', imageAnimOut = 'fade',
  text, textSize = 'body-lg', textWidth = 'grid', textWidthCustom = 60,
  textPosition = 'center', textAlign = 'center',
  textAnimIn = 'fade', textAnimOut = 'fade',
  marginTop = 96, marginBottom = 96, bgColor = '', textColor = '',
}) {
  const imageRef = useScrollReveal({ animIn: imageAnimIn, animOut: imageAnimOut });
  const textRef = useScrollReveal({ animIn: textAnimIn, animOut: textAnimOut });

  const textWidthClass = textWidth === 'custom' ? '' : (TEXT_WIDTH_CLASS[textWidth] || TEXT_WIDTH_CLASS.grid);
  const textWidthStyle = textWidth === 'custom' ? { maxWidth: textWidthCustom + '%' } : undefined;

  return (
    <section
      className="px-6 md:px-16 text-theme-text"
      style={{ paddingTop: marginTop, paddingBottom: marginBottom, backgroundColor: bgColor || undefined, color: textColor || undefined }}
    >
      <div className="max-w-6xl mx-auto flex flex-col gap-10">
        {image && (
          <div className={'flex ' + JUSTIFY_CLASS[imagePosition]}>
            <img
              ref={imageRef}
              src={image}
              alt={imageAlt}
              style={{
                width: imageWidth + '%',
                marginTop: imageMarginTop,
                marginRight: imageMarginRight,
                marginBottom: imageMarginBottom,
                marginLeft: imageMarginLeft,
              }}
              className="rounded-lg object-cover"
              loading="lazy"
              decoding="async"
            />
          </div>
        )}
        {text && (
          <div className={'flex ' + JUSTIFY_CLASS[textPosition]}>
            <p
              ref={textRef}
              className={TEXT_SIZE_CLASS[textSize] + ' leading-relaxed ' + textWidthClass}
              style={{ textAlign, ...textWidthStyle }}
              dangerouslySetInnerHTML={{ __html: renderTextMarkup(text) }}
            />
          </div>
        )}
      </div>
    </section>
  );
}
